import React from 'react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Paper, Typography } from '@mui/material';

interface HealthChartProps {
  data: any[];
  type: string;
}

const HealthChart: React.FC<HealthChartProps> = ({ data, type }) => {
  if (!data || data.length === 0) {
    return (
      <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
        Aucune donnée disponible pour ce type de mesure
      </Typography>
    );
  }

  // Formater les données pour le graphique
  const chartData = data.map((item) => ({
    date: new Date(item.measuredAt).toLocaleDateString(),
    valeur: type === 'tension' ? item.systolic : item.value,
    diastolique: item.diastolic,
    temps: new Date(item.measuredAt).toLocaleTimeString(),
  })).reverse();

  const getYAxisLabel = () => {
    switch (type) {
      case 'tension': return 'mmHg';
      case 'glycemie': return 'mg/dL';
      case 'oxygenation': return '%';
      case 'poids': return 'kg';
      case 'temperature': return '°C';
      case 'cardiaque': return 'bpm';
      default: return '';
    }
  };

  const getTitle = () => {
    const titles: Record<string, string> = {
      tension: 'Tension artérielle',
      glycemie: 'Glycémie',
      oxygenation: 'Saturation en Oxygène',
      poids: 'Poids',
      temperature: 'Température',
      cardiaque: 'Fréquence cardiaque',
    };
    return titles[type] || type;
  };

  const getColor = () => {
    const colors: Record<string, string> = {
      tension: '#8884d8',
      glycemie: '#82ca9d',
      oxygenation: '#ffc658',
      poids: '#ff7300',
      temperature: '#ff4d4d',
      cardiaque: '#d84c8a',
    };
    return colors[type] || '#8884d8';
  };

  return (
    <Paper sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        📈 Évolution de la {getTitle()}
      </Typography>
      <ResponsiveContainer width="100%" height={400}>
        {type === 'tension' ? (
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis label={{ value: getYAxisLabel(), angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="valeur" name="Systolique" stroke="#8884d8" strokeWidth={2} />
            <Line type="monotone" dataKey="diastolique" name="Diastolique" stroke="#82ca9d" strokeWidth={2} />
          </LineChart>
        ) : (
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis label={{ value: getYAxisLabel(), angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="valeur" name={getTitle()} stroke={getColor()} fill={getColor()} fillOpacity={0.3} />
          </AreaChart>
        )}
      </ResponsiveContainer>
    </Paper>
  );
};

export default HealthChart;